import React, { useEffect, useReducer } from 'react';
import { useIntl } from 'react-intl';
import FeedbackCard from './FeedbackCard';
import feedbackCarouselMessages from './messages';

import './FeedbackCarousel.scss';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');

const { Carousel } = require('@edx/paragon');

interface Item {
  id: number,
  attributes: {
    rating_content: number,
    feedback: string,
  },
  relationships: {
    author: {
      data: Author
    },
    course_id: {
      data: CourseOverview
    }
  }
}

interface JsonApiItem {
  id: number,
  type: string,
  attributes: object
}

interface Author extends JsonApiItem {
  attributes: {
    username: string,
    profile_name: string
  }
}

interface CourseOverview extends JsonApiItem {
  attributes: {
    display_name: string
  }
}

interface State {
  chunks: React.ReactNode[];
  chunkSize: number;
  items: Item[];
}

interface Action {
  type: string;
  chunks: React.ReactNode[];
  chunkSize: number;
  items: Item[];
}

interface Data {
  data: Item[];
}

interface OnSuccess {
  (data?: Object): void;
}

interface OnFailure {
  (error?: Error): void;
}

/**
 * Function that specifies how the state gets updated.
 * @param  {State} state   Current state.
 * @param  {Action} action Describes kind of action to be executed.
 * @return {Object}        Updated state based on the action type.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_CHUNKS':
      return {
        ...state,
        chunks: action.chunks,
      };
    case 'SET_CHUNK_SIZE':
      return {
        ...state,
        chunkSize: action.chunkSize,
      };
    case 'SET_ITEMS':
      return {
        ...state,
        items: action.items,
      };
    default:
      return state;
  }
}

/**
 * This makes a GET request and allow to execute a function in case on success or on failure.
 * @param  {String}       path       String that contains the API Path the base url is COURSE_EXPERIENCE_API_URL.
 * @param  {Object}       body       Body of the POST request.
 * @param  {Object}       apiConfig  Extra configuration, likes headers.
 * @param  {OnSuccess}    onSuccess  Function that will be executed on 20X responses.
 * @param  {OnFailure}    onFailure  Function that will be executed on responses different from 20X responses.
 */
function getData(path: string, apiConfig: Object, onSuccess: OnSuccess, onFailure: OnFailure) {
  const { COURSE_EXPERIENCE_API_URL } = getConfig();
  const url = `${COURSE_EXPERIENCE_API_URL}/${path}/?page[size]=12&sort=-rating_content`;

  getAuthenticatedHttpClient().get(url, apiConfig).then(({ data }: { data: Data }) => {
    onSuccess(data);
  }, (error: Error) => {
    onFailure(error);
  });
}

/**
 * Determines the chunk size based on the current window width.
 * The chunk size represents the number of elements to be displayed together as a group.
 *
 * @returns {number} - The calculated chunk size.
 */
function getChunkSize(): number {
  let chunkSize = 3;

  if (window.innerWidth > 860 && window.innerWidth < 1250) {
    chunkSize = 2;
  } else if (window.innerWidth < 860) {
    chunkSize = 1;
  }

  return chunkSize;
}

/**
 * React function component that displays multiple feedback card components in a row. The
 * number of cards per row depends on the window width, and the content is retrieved from
 * the public feedback API.
 *
 * @return {JSX.Element}               React element.
 */
function FeedbackCarousel(): JSX.Element {
  const initialState = {
    chunks: [],
    chunkSize: getChunkSize(),
    items: [],
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const apiConfig = { headers: { accept: 'application/vnd.api+json,application/json' } };
  const intl = useIntl();

  useEffect(() => {
    const path = 'feedback/public/courses';
    const getOnSuccess = (responseData: Data) => {
      // On success, the carousel items will be displayed, allowing multiple feedbacks to be shown.
      // the items will be filtered out based on the feedback content.
      const items = responseData.data.filter((item: Item) => item.attributes.feedback);
      dispatch({ type: 'SET_ITEMS', ...state, items });
    };
    const getOnFailure = (error: Error) => {
      // On failure the carousel won't be displayed, this just logs the error message.
      // eslint-disable-next-line no-console
      console.error('Unable to set feedback section with error:', error.message);
    };
    // Retrive data from public feedback API
    getData(path, apiConfig, getOnSuccess, getOnFailure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  window.addEventListener(
    'resize',
    () => { dispatch({ type: 'SET_CHUNK_SIZE', ...state, chunkSize: getChunkSize() }); },
  );

  useEffect(() => {
    const numItems = state.items.length;
    let startIndex = 0;
    let endIndex = Math.min(state.chunkSize, numItems);

    const chunks = [];

    while (startIndex < numItems) {
      const chunkItems = state.items.slice(startIndex, endIndex);
      chunks.push(
        <Carousel.Item key={startIndex}>
          <div className="feedback-carousel-row">
            {chunkItems.map((item: Item) => {
              const { attributes, relationships } = item;
              const username = relationships.author.data.attributes.profile_name
                             || relationships.author.data.attributes.username;

              return (
                <FeedbackCard
                  key={item.id}
                  username={username}
                  feedback={attributes.feedback}
                  rating={attributes.rating_content}
                  coursename={relationships.course_id.data.attributes.display_name}
                />
              );
            })}
          </div>
        </Carousel.Item>,
      );

      startIndex = endIndex;
      endIndex = Math.min(endIndex + state.chunkSize, numItems);
    }

    dispatch({ type: 'SET_CHUNKS', ...state, chunks });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.chunkSize, state.items]);

  return (
    <div className="feedback-container">
      {state.chunks.length > 0 && (
        <>
          <h4 className="feedback-carousel-title">{intl.formatMessage(feedbackCarouselMessages.reviews)}</h4>
          <Carousel className="feedback-carousel" controls={false}>
            {state.chunks}
          </Carousel>
        </>
      )}
    </div>
  );
}

export default FeedbackCarousel;
